import { useMutation } from "@apollo/client";
import { SearchIcon, TextInput } from "@src/components/elements";
import { ArrowIcon } from "@src/components/elements/assets/ArrowIcon";
import { PermissionChips } from "@src/components/modules/PermissionChips/PermissionChips";
import { useAuth } from "@src/contexts/AuthProvider";
import { notifySuccess } from "@src/shared/notify";
import { gql } from "@src/__generated__";
import { ImpersonationUserFragment } from "@src/__generated__/graphql";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components/macro";
import { useSudoziInfiniteQuery } from "@src/infrastructure/useSudoziQuery";
import { userName } from "@src/utils/userName";
import { UserName } from "@src/components/modules/UserName";
import { GetMembersRoute } from "@src/sudozi-schemas/api-route-schemas";

gql(/* GraphQL */ `
  fragment ImpersonationUser on UserObject {
    id
    email
    firstName
    lastName
    hasAdminAccess
    hasSuperViewerAccess
    hasDepartmentAccess
    hasBusinessUnitAccess
    hasDepartmentPayrollAccess
    hasBusinessUnitPayrollAccess
  }
`);

const CREATE_IMPERSONATION_TOKEN_MUTATION = gql(/* GraphQL */ `
  mutation CreateImpersonationTokenMutation($userId: String!) {
    createImpersonationToken(userId: $userId)
  }
`);

const Style = styled.div`
  margin: 1rem;
  max-height: calc(100vh - 135px);
  overflow: auto;

  .user {
    justify-content: space-between;
    padding: 1rem;
    border-top: 0.25px solid var(--gray);
    border-collapse: collapse;
    cursor: pointer;
  }

  .user:last-child {
    border-bottom: 0.25px solid var(--gray);
  }

  .user:hover {
    background-color: var(--background-gray);
  }

  .search-icon {
    width: 2rem;
  }
`;

export const ImpersonationModal: React.FC = () => {
  const { data, fetchNextPage, hasNextPage } = useSudoziInfiniteQuery({
    route: GetMembersRoute,
    queryKey: "GET /api/members",
    query: { enabled: "true" },
  });

  useEffect(() => {
    if (data && hasNextPage) {
      void fetchNextPage();
    }
  }, [data, fetchNextPage, hasNextPage]);

  const allUsers = useMemo(
    () =>
      data?.pages.flatMap((page) => page.edges.flatMap((e) => e.node)) ?? [],
    [data?.pages],
  );

  const [createImpersonationToken] = useMutation(
    CREATE_IMPERSONATION_TOKEN_MUTATION,
  );
  const [filterText, setFilterText] = useState("");
  const { updateCurrentUserState } = useAuth();

  const users = useMemo(() => {
    const lowerInput = filterText.toLowerCase();
    return (
      allUsers.filter(
        (u) =>
          u.firstName?.toLowerCase().includes(lowerInput) ||
          u.lastName?.toLowerCase().includes(lowerInput),
      ) ?? []
    );
  }, [filterText, allUsers]);

  async function fetchTokenAndReload(user: ImpersonationUserFragment) {
    await createImpersonationToken({
      variables: { userId: user.id },
    });
    await updateCurrentUserState();
    notifySuccess(`You are now viewing as ${userName(user)}`);
  }

  return (
    <Style>
      <div className="filter-area">
        <TextInput
          value={filterText}
          name="search"
          placeholder="Search & filter"
          onChange={(event) => setFilterText(event.target.value)}
          prefix={<SearchIcon className="search-icon" />}
        />
      </div>
      <div className="users">
        {users.map((user) => (
          <div
            key={user.id}
            className="user row"
            onClick={() => fetchTokenAndReload(user)}
          >
            <div className="row">
              <UserName user={user} />
              <PermissionChips permissions={user} />
            </div>
            <ArrowIcon />
          </div>
        ))}
      </div>
    </Style>
  );
};
